import React, {
  useState,
  useEffect,
  useCallback,
  useRef,
  lazy,
  Suspense,
} from 'react';

// Redux
import { useDispatch, useSelector } from 'react-redux';

// date-fns

// Material
import Grid from '@material-ui/core/Grid';
import Tabs from '@material-ui/core/Tabs';
import Tab from '@material-ui/core/Tab';
import Box from '@material-ui/core/Box';

// CSVLink
import { CSVLink } from 'react-csv';

// Helpers
import { oneWeekAgo, returnDateAndTime, formatDateAndTime } from 'helpers/date';
import { getComparator, stableSort } from 'helpers/tableSortLabels';
import { currencyFormat } from 'helpers/currencyFormat';

// Actions
import { setOpen, setClose } from 'store/ducks/dialogs/actions';
import { importaionsReportRequest } from 'store/ducks/company/actions';

// Types
import { ApplicationState } from 'store';
import { ImportationsReportInterface } from 'store/ducks/company/types';

// Constants
import ITEMS from 'constants/itemsByFilter';
import TITLES_OPTIONS from 'constants/infoModulesTitle';

// Components
import {
  MainContent,
  DialogueExport,
  SearchComponent,
  XlsHeaderInterface,
  XlsRowInterface,
  ReportTitle,
} from 'components';
import { TablePlaceholder } from 'components/TablePlaceholder';
import { TabPanel } from 'components/TabPanel';
import { REPORTS_TYPES } from 'constants/report';
import { DetailedImportationsReport } from './components/DetailedImportationsReport';
import { Submissions, History } from '../../../../components';

// Styles
import useStyles from './ImportationsReportDatails.styles';

// Lazy Components
const Filter = lazy(() => import('components/Filter'));
const TotalCountComponent = lazy(() =>
  import('components/TotalCountComponent'),
);

interface ImportationsReportDatailsProps {
  setSubmissionData: (param: any) => void;
  setIsAddSubmissiion: (param: any) => void;
  setIsEditSubmissiion: (param: any) => void;
}

const ImportationsReportDatails: React.FC<ImportationsReportDatailsProps> = ({
  setSubmissionData,
  setIsAddSubmissiion,
  setIsEditSubmissiion,
}) => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const csvRef = useRef(null);

  const [tableCSV, setTableCSV] = useState('');
  const [reportIsVisible, setReportIsVisible] = useState(false);
  const { oneDayAgo: startDate, endDate } = oneWeekAgo;
  const [tableReference, setTableReference] = useState();
  const [generalNavGuide, setGeneralNavGuide] = useState(0);
  const [importationList, setImportationList] = useState<
    ImportationsReportInterface[]
  >();

  const [periodString, setPeriodString] = useState('');
  const [xlsHeader, setXlsHeader] = useState<XlsHeaderInterface[]>();
  const [xlsRows, setXlsRows] = useState<Array<XlsRowInterface[]>>([]);

  const [simplePdfData, setSimplePdfData] = useState<{
    title: string;
    subtitle: string;
    filters: string;
    headers: any;
    rows: any;
  }>({
    title: '',
    subtitle: '',
    filters: '',
    headers: [],
    rows: [],
  });

  const initialImportationsReport = useSelector(
    (state: ApplicationState) => state.companyReport.importationsReport,
  );

  const transportOperatorSelected = useSelector(
    (state: ApplicationState) =>
      state.transportOperators.transportOperatorSelected,
  );

  const transportOperatorsList = useSelector(
    (state: ApplicationState) =>
      state.transportOperators.transportOperatorsList,
  );

  const handleChange = (event: React.ChangeEvent<{}>, newValue: number) => {
    setGeneralNavGuide(newValue);
  };

  const handleLoadData = useCallback(() => {
    setReportIsVisible(true);
    dispatch(
      importaionsReportRequest({
        startDate: new Date().setHours(0, 0, 0),
        endDate: new Date().setHours(23, 59, 59),
      }),
    );

    const tempPeriodString = `Dia: ${new Date().toLocaleDateString('pt-BR', {
      dateStyle: 'short',
    })}`;

    setPeriodString(tempPeriodString);
    // setInitialFilters();
  }, [dispatch, endDate]);

  const handleApply = useCallback(
    (data: any) => {
      dispatch(setClose({}));
      setReportIsVisible(true);

      const { startDate, endDate, startedAt, selectedFilename } = data;

      const isPeriod = startDate?.getTime() < endDate?.getTime();
      dispatch(
        importaionsReportRequest({
          startDate: isPeriod
            ? startDate.setHours(0, 0, 0).toString()
            : startedAt.setHours(0, 0, 0).toString(),
          endDate: isPeriod
            ? endDate.setHours(23, 59, 59).toString()
            : startedAt.setHours(23, 59, 59).toString(),
          filename: selectedFilename || undefined,
        }),
      );

      const tempPeriodString = `De: ${
        isPeriod
          ? formatDateAndTime(new Date(startDate))
          : formatDateAndTime(new Date(startedAt))
      } até: ${
        isPeriod
          ? formatDateAndTime(new Date(endDate))
          : formatDateAndTime(new Date(startedAt))
      }`;

      const updatedPeriodString = tempPeriodString;

      setPeriodString(updatedPeriodString);
    },
    [dispatch],
  );

  // generating the table csv
  useEffect(() => {
    let csv = '';
    if (
      Array.isArray(initialImportationsReport) &&
      initialImportationsReport.length > 0
    ) {
      let header = `Nome do Arquivo;Empresa;Valor;NSU;Data;Url`;
      if (transportOperatorSelected.type === 1) {
        header += ';Operação\n';
      } else {
        header += '\n';
      }
      csv += header;
      csv += stableSort(
        initialImportationsReport,
        getComparator('desc', 'createdAt'),
      )
        .map(
          (importation) =>
            `${importation.fileName};${importation.company};${currencyFormat({
              value: Number(importation.value) || 0,
            })};${importation.nsu};${returnDateAndTime(
              importation.createdAt,
            )};${importation.fileUrl};${
              transportOperatorSelected.type === 1
                ? transportOperatorsList.find(
                    (e) => e.id === importation?.transportOperatorId,
                  )?.name + '\n' || '---'
                : '\n'
            }`,
        )
        .join('');
      setTableCSV(csv);
    }
  }, [initialImportationsReport]);

  useEffect(() => {
    setImportationList(initialImportationsReport);
  }, [initialImportationsReport]);

  useEffect(() => {
    const headerSimplePdf = [
      'Nome do Arquivo',
      'Empresa',
      'Valor',
      'NSU',
      'Data',
      'Url',
    ];

    const rowsSimplePdf =
      initialImportationsReport?.length > 0
        ? initialImportationsReport?.map((item: any) => [
            item?.fileName ? item?.fileName : '-',
            item?.company ? item?.company : '-',
            item?.value
              ? currencyFormat({
                  value: Number(item?.value) || 0,
                })
              : '-',
            item?.nsu ? item?.nsu : '-',
            item?.createdAt
              ? new Date(item?.createdAt).toLocaleString('pt-BR')
              : '-',
            item?.fileUrl ? item?.fileUrl : '-',
          ])
        : [];

    const tempSimplePdfData = {
      title: 'ONBOARD',
      subtitle: 'Relatório de Entradas',
      filters: periodString || '',
      headers: headerSimplePdf,
      rows: rowsSimplePdf,
    };

    setSimplePdfData(tempSimplePdfData);
  }, [initialImportationsReport, periodString]);

  useEffect(() => {
    const staticXlsheader = [
      {
        header: 'Nome do Arquivo',
        key: 'fileName',
        width: 20,
      },
      {
        header: 'Empresa',
        key: 'company',
        width: 20,
      },
      {
        header: 'Valor',
        key: 'value',
        width: 20,
      },
      {
        header: 'NSU',
        key: 'nsu',
        width: 20,
      },
      {
        header: 'Data',
        key: 'createdAt',
        width: 20,
      },
      {
        header: 'Url',
        key: 'url',
        width: 20,
      },
    ];

    if (transportOperatorSelected.type === 1) {
      staticXlsheader.push({
        header: 'Operação',
        key: 'operator',
        width: 20,
      });
    }
    setXlsHeader(staticXlsheader);

    const rows = stableSort(
      initialImportationsReport,
      getComparator('desc', 'createdAt'),
    ).map((data) => {
      const staticXlsRows = [
        {
          key: 'fileName',
          value: `${data?.fileName ? data?.fileName : '-'}`,
          image: null,
        },
        {
          key: 'company',
          value: `${data?.company ? data?.company : '-'}`,
          image: null,
        },
        {
          key: 'value',
          value: `${
            data?.value
              ? currencyFormat({
                  value: Number(data?.value) || 0,
                })
              : '-'
          }`,
          image: null,
        },
        {
          key: 'nsu',
          value: `${data?.nsu ? data?.nsu : '-'}`,
          image: null,
        },
        {
          key: 'createdAt',
          value: `${
            data?.createdAt
              ? new Date(data?.createdAt).toLocaleString('pt-BR')
              : '-'
          }`,
          image: null,
        },
        {
          key: 'url',
          value: `${data?.fileUrl ? data?.fileUrl : '-'}`,
          image: null,
        },
      ];

      if (transportOperatorSelected.type === 1) {
        staticXlsRows.push({
          key: 'operator',
          value: `${transportOperatorsList.find(
            (e) => e.id === data?.transportOperatorId,
          )?.name || '---'}`,
          image: null,
        });
      }
      return staticXlsRows;
    }) as XlsRowInterface[][];
    setXlsRows(rows);
  }, [initialImportationsReport]);

  return (
    <div className={classes.root}>
      <MainContent className={classes.paperRoot}>
        <Grid
          item
          justifyContent='space-between'
          className={classes.gridContainer}
          ref={(node: any) => {
            setTableReference(node);
          }}>
          <Grid item>
            <ReportTitle
              titleVariant='h4'
              title='Relatórios de Empresas • Importações'
              tooltipTitle={TITLES_OPTIONS.REPORTS.COMPANYS.IMPORTS.VALUE}
            />
          </Grid>
          <Grid item className={classes.icons}>
            <Grid item>
              <CSVLink
                filename='tabela_relatorio_de_importações.csv'
                data={tableCSV}
                ref={csvRef}
              />
            </Grid>
          </Grid>
          <Grid container direction='column'>
            <Grid item className={classes.scopeFilter}>
              <Box display='flex' alignItems='center' marginTop='8px'>
                <SearchComponent
                  disabledInput={!reportIsVisible}
                  data={initialImportationsReport}
                  itemsByFilter={ITEMS.ReportImportation}
                  results={(data: any[]) => {
                    setImportationList(data);
                  }}
                  modalFilter={() => {
                    dispatch(
                      setOpen({
                        open: true,
                        content: (
                          <Suspense fallback={<p>Carregando...</p>}>
                            <Filter
                              fields={ITEMS.importations.remote}
                              handleApply={handleApply}
                            />
                          </Suspense>
                        ),
                        maxWidth: 'xl',
                      }),
                    );
                  }}
                />

                {/* <Grid container justifyContent='flex-end'>
                  <Button
                    className={classes.addButton}
                    onClick={() => setIsAddSubmissiion(true)}>
                    <Typography style={{ marginRight: 8, fontSize: 20 }}>
                      +
                    </Typography>{' '}
                    <Typography style={{ fontSize: 12 }}>
                      Programar Novo
                    </Typography>
                  </Button>
                </Grid> */}
              </Box>
            </Grid>
          </Grid>
          <Tabs
            indicatorColor='primary'
            onChange={handleChange}
            style={{
              background: '#ffffff',
              margin: '10px 0px 32px 0px',
            }}
            textColor='primary'
            value={generalNavGuide}>
            <Tab label='Relatório' />
            <Tab label='Envios' />
            <Tab label='Histórico' />
          </Tabs>
          <TabPanel index={0} value={generalNavGuide}>
            {!reportIsVisible && <TablePlaceholder onClick={handleLoadData} />}
            {reportIsVisible && (
              <>
                <Grid className={classes.contentDilogueExport}>
                  <Box
                    display='flex'
                    justifyContent='flex-end'
                    alignItems='center'>
                    {initialImportationsReport?.length! > 0 && (
                      <Box
                        width='100%'
                        display='flex'
                        justifyContent='flex-end'
                        mr={2}>
                        <Suspense fallback='Carregando...'>
                          <TotalCountComponent
                            count={initialImportationsReport?.length || 0}
                            title='arquivos'
                            position='right'
                          />
                        </Suspense>
                      </Box>
                    )}

                    <DialogueExport
                      tableInstance={tableReference}
                      csvRef={csvRef}
                      simplePdf
                      simplePdfData={simplePdfData}
                      columnsPerPage={5}
                      pdfName='relatorio_de_empresas_importacoes'
                      xlsTitle='relatorio_de_empresas_importacoes'
                      xlsHeader={xlsHeader}
                      xlsRows={xlsRows}
                      pdf
                      csv
                      xls
                    />
                  </Box>
                </Grid>
                <DetailedImportationsReport
                  setTableReference={setTableReference}
                  importationList={importationList}
                />
              </>
            )}
          </TabPanel>
          <TabPanel index={1} value={generalNavGuide}>
            <Submissions
              setSubmissionData={setSubmissionData}
              setIsEditSubmissiion={setIsEditSubmissiion}
              reportType={REPORTS_TYPES.CHARGE_VAN_FILE}
            />
          </TabPanel>{' '}
          <TabPanel index={2} value={generalNavGuide}>
            <History reportType={REPORTS_TYPES.CHARGE_VAN_FILE} />
          </TabPanel>
        </Grid>
      </MainContent>
    </div>
  );
};

export default ImportationsReportDatails;
export { ImportationsReportDatails };
